import { observer } from 'mobx-react-lite';

import { Check } from '@fluentui/react/lib/Check';
import type { FileIcon } from 'owa-file-icon';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import * as React from 'react';

import styles from './ItemTileView.scss';
import classNames from 'classnames';

export interface ItemTileViewPropsBase {
    itemIndex: number;
    itemType: number;
    isSelected: boolean;
    primaryText: string;
    location: string;
    ariaLabel: string;
    icon: FileIcon;
    secondaryText?: string;
    className?: string;
    onSelectedClassName?: string;
}

export interface ItemTileViewProps extends ItemTileViewPropsBase {
    children?: JSX.Element;
    shouldShowTileOverlay?: boolean;
}

export default observer(function ItemTileView(props: ItemTileViewProps) {
    React.useEffect(() => {
        if (props.itemIndex === 0) {
            focus();
        }
    }, []);
    const tileContainer = React.useRef<HTMLDivElement>();
    const focus = () => {
        tileContainer.current.focus();
    };
    const focusOnTileViewOnMouseDown = (evt: React.MouseEvent<unknown>) => {
        // If a click happens on the child container then we need to change the focus
        // back to the parent container of the tile view.
        evt.stopPropagation();
        evt.preventDefault();
        focus();
    };
    // If the overlay is not shown the child container takes up the whole width and height otherwise it just takes whats available
    const childContainerClassName = props.shouldShowTileOverlay
        ? styles.childContainerPartial
        : styles.childContainerFull;
    const checkboxClassNames = classNames([
        styles.checkbox,
        props.isSelected && styles.showCheckbox,
    ]);
    const tilePrimaryTextOverlayClassName = classNames([
        styles.tilePrimaryTextOverlay,
        props.isSelected && styles.tilePrimaryTextOverlayOnSelected,
    ]);
    const tileSecondaryTextOverlayClassName = classNames([
        styles.tileSecondaryTextOverlay,
        props.isSelected && styles.tileSecondaryTextOverlayOnSelected,
    ]);
    const itemTitle =
        props.itemType === AttachmentDataProviderType.OneDrivePro
            ? props.location
            : props.primaryText;
    return (
        <div
            ref={ref => (tileContainer.current = ref)}
            className={classNames([
                styles.container,
                props.className,
                props.isSelected && props.onSelectedClassName,
            ])}
            aria-label={props.ariaLabel}
            data-is-focusable={true}
            tabIndex={-1}
            data-selection-index={props.itemIndex}
            title={itemTitle}>
            <div className={styles.tile}>
                <div className={checkboxClassNames} data-selection-toggle={true}>
                    <Check checked={props.isSelected} />
                </div>

                <div
                    className={childContainerClassName}
                    data-is-focusable={false}
                    onMouseDown={focusOnTileViewOnMouseDown}>
                    {props.children}
                </div>

                {props.shouldShowTileOverlay && (
                    <div className={styles.tileOverlay}>
                        <div className={tilePrimaryTextOverlayClassName}> {props.primaryText} </div>
                        {props.secondaryText && (
                            <div className={tileSecondaryTextOverlayClassName}>
                                {' '}
                                {props.secondaryText}{' '}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
});
